<template>
  <div>
    <NavBar />
    <div class="page planning-page">

      <div class="page-header planning-hero">
        <div>
          <p class="page-kicker">Organisation marchés</p>
          <h1 class="page-title">Planning</h1>
          <p class="page-subtitle">Pilotez les marchés programmés, les horaires, les villes et les équipes du mois.</p>
        </div>
        <div class="header-actions">
          <button class="btn btn-secondary action-btn" @click="ouvrirGenerateur">
            Générer un planning
          </button>
          <button class="btn btn-primary action-btn" @click="ouvrirCreer()">
            Ajouter un marché
          </button>
        </div>
      </div>

      <div class="planning-shell">
        <!-- Navigation mois -->
        <div class="calendar-toolbar">
          <div class="mois-nav">
            <button class="nav-btn" @click="moisPrecedent" aria-label="Mois précédent">‹</button>
            <span class="mois-titre">{{ moisAffiche }}</span>
            <button class="nav-btn" @click="moisSuivant" aria-label="Mois suivant">›</button>
          </div>
          <button class="btn-aujourd-hui" @click="allerAujourdhui">Aujourd'hui</button>
        </div>

        <!-- Légende -->
        <div class="legende">
          <span class="leg-item"><span class="leg-dot ev-orange"></span> Sète</span>
          <span class="leg-item"><span class="leg-dot ev-violet"></span> Mèze</span>
          <span class="leg-item"><span class="leg-dot ev-bleu"></span> Pézenas</span>
          <span class="leg-item"><span class="leg-dot ev-vert"></span> La Grande Motte</span>
          <span class="leg-item"><span class="leg-dot ev-gris"></span> Autre</span>
        </div>
      </div>

      <div class="loading-card" v-if="loading">
        <span class="loading-dot"></span>
        Chargement du planning...
      </div>

      <!-- Calendrier -->
      <div class="calendrier">
        <div class="cal-header">
          <div v-for="j in joursLabels" :key="j" class="cal-jour-label">{{ j }}</div>
        </div>
        <div class="cal-grid">
          <div
            v-for="(jour, idx) in grille"
            :key="idx"
            class="cal-cell"
            :class="{
              'autre-mois':   !jour.courant,
              'aujourd-hui':  jour.estAujourdhui,
              'a-evenements': jour.evenements.length > 0
            }"
            @click="jour.courant && clicJour(jour)"
          >
            <div class="cal-num">{{ jour.numero }}</div>
            <div
              v-for="ev in jour.evenements.slice(0, 3)"
              :key="ev.id"
              class="cal-event"
              :class="['ev-' + couleurVille(ev.ville), { 'ev-recurrent': ev.recurrent }]"
              @click.stop="ouvrirDetail(ev)"
            >
              <span class="ev-heure">{{ ev.heure_debut.substring(0,5) }}</span>
              <span class="ev-ville">{{ libelleLieu(ev) }}</span>
              <span class="cal-flags" v-if="estJourFerie(ev) || zonesVacances(ev).length || afficherFallbackVacances(ev)">
                <span v-if="estJourFerie(ev)" class="cal-flag flag-ferie" :title="libelleFerie(ev, false)">F</span>
                <span
                  v-for="zone in zonesVacances(ev)"
                  :key="zone"
                  class="cal-flag flag-vacances"
                  :title="libelleVacancesZones(ev, false)"
                >{{ zone }}</span>
                <span v-if="afficherFallbackVacances(ev)" class="cal-flag flag-vacances" :title="libelleVacances(ev, false)">V</span>
              </span>
            </div>
            <div class="cal-plus" v-if="jour.evenements.length > 3">
              +{{ jour.evenements.length - 3 }}
            </div>
          </div>
        </div>
      </div>

      <!-- Liste du mois -->
      <div class="card liste-card" v-if="tousLesEvenements.length">
        <div class="section-heading">
          <div>
            <p class="section-kicker">Vue détaillée</p>
            <h3>Marchés de {{ moisAffiche }}</h3>
          </div>
          <span class="count-pill">{{ tousLesEvenements.length }} marché{{ tousLesEvenements.length > 1 ? 's' : '' }}</span>
        </div>
        <div class="ev-liste">
          <div
            class="ev-item"
            v-for="ev in tousLesEvenements"
            :key="ev.id"
            :class="['border-' + couleurVille(ev.ville), { 'ev-item-recurrent': ev.recurrent }]"
          >
            <div class="ev-date-col">
              <div class="ev-jour-sem">{{ jourSemaine(ev.date) }}</div>
              <div class="ev-date-num">{{ formatDateCourt(ev.date) }}</div>
            </div>
            <div class="ev-info">
              <div class="ev-titre">
                <span class="ev-badge" :class="'bg-' + couleurVille(ev.ville)">
                  📍 {{ libelleLieu(ev) }}
                  <em v-if="ev.recurrent"> · récurrent</em>
                </span>
                <span class="ev-horaires">{{ ev.heure_debut.substring(0,5) }} → {{ ev.heure_fin.substring(0,5) }}</span>
              </div>
              <div class="ev-contextes" v-if="estJourFerie(ev) || zonesVacances(ev).length || afficherFallbackVacances(ev)">
                <span v-if="estJourFerie(ev)" class="context-badge context-ferie">
                  {{ libelleFerie(ev, false) }}
                </span>
                <span v-if="zonesVacances(ev).length" class="context-badge context-vacances">
                  Vacances zones : {{ zonesVacances(ev).join(', ') }}
                </span>
                <span v-if="zonesVacances(ev).length && nomsVacancesZones(ev).length" class="context-badge context-vacances-name">
                  Nom : {{ nomsVacancesZones(ev).join(' / ') }}
                </span>
                <span v-if="afficherFallbackVacances(ev)" class="context-badge context-vacances">
                  {{ libelleVacances(ev, false) }}
                </span>
              </div>
              <div class="ev-notes" v-if="ev.notes">{{ ev.notes }}</div>
              <div class="ev-personnes" v-if="ev.personnes && ev.personnes.length">
                <span class="personne-chip" v-for="p in ev.personnes" :key="p.id || p.nom">
                  👤 {{ p.nom }} <span class="personne-role">{{ p.role }}</span>
                </span>
              </div>
            </div>
            <div class="ev-actions">
              <button class="btn-action" @click="ouvrirDetail(ev)" title="Modifier">✏️</button>
              <button class="btn-action delete" @click="supprimer(ev.id)" v-if="!ev.recurrent" title="Supprimer">🗑️</button>
            </div>
          </div>
        </div>
      </div>

      <div class="card vide-card" v-else-if="!loading">
        <div class="empty-icon">+</div>
        <h3>Aucun marché ce mois-ci</h3>
        <p>Ajoutez un marché ponctuel ou générez un planning récurrent pour remplir ce mois.</p>
        <button class="btn btn-primary empty-action" @click="ouvrirCreer()">Ajouter un marché</button>
      </div>

    </div>

    <!-- Popup créer / éditer -->
    <div class="modal-overlay" v-if="popup" @click.self="fermerPopup">
      <div class="modal">
        <div class="modal-heading">
          <p class="section-kicker">{{ modeEdition ? 'Edition' : 'Création' }}</p>
          <h3>{{ modeEdition ? 'Modifier le marché' : 'Nouveau marché' }}</h3>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label class="label">Date *</label>
            <input v-model="form.date" class="input" type="date" />
          </div>
          <div class="form-group">
            <label class="label">Ville *</label>
            <div class="commune-autocomplete">
              <input
                v-model="form.ville"
                class="input"
                placeholder="Tapez une ville ou un code postal"
                autocomplete="off"
                @input="onFormVilleInput"
                @focus="chercherCommunesForm"
              />
              <div class="commune-suggestions" v-if="formSuggestionsOuvertes">
                <div class="commune-option commune-option-state" v-if="communesLoadingForm">
                  <span>Recherche...</span>
                </div>
                <button
                  v-for="commune in communesForm"
                  :key="commune.code_insee + '-' + codePostalCommune(commune)"
                  type="button"
                  class="commune-option"
                  @click="selectionnerCommuneForm(commune)"
                >
                  <span>{{ commune.nom }}</span>
                  <strong>{{ codesPostauxLabel(commune) }}</strong>
                </button>
                <div class="commune-option commune-option-state" v-if="formRechercheTerminee && !communesLoadingForm && communesForm.length === 0">
                  <span>Aucune commune trouvée</span>
                </div>
                <button
                  v-if="form.ville"
                  type="button"
                  class="commune-option manual"
                  @click="utiliserVilleManuelleForm"
                >
                  <span>Utiliser cette ville manuellement</span>
                  <strong>{{ form.ville }}</strong>
                </button>
              </div>
              <p class="commune-state selected" v-else-if="form.code_insee">
                Commune officielle sélectionnée : {{ form.ville }} — {{ form.code_postal }}
              </p>
            </div>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label class="label">Heure début *</label>
            <input v-model="form.heure_debut" class="input" type="time" />
          </div>
          <div class="form-group">
            <label class="label">Heure fin *</label>
            <input v-model="form.heure_fin" class="input" type="time" />
          </div>
        </div>

        <div class="form-group">
          <label class="label">Notes</label>
          <input v-model="form.notes" class="input" placeholder="Ex: Apporter la tente bleue..." />
        </div>

        <div class="personnes-section">
          <label class="label">Personnes présentes</label>
          <div class="personne-ligne" v-for="(p, i) in form.personnes" :key="i">
            <input v-model="p.nom" class="input" placeholder="Prénom Nom" style="flex:2" />
            <select v-model="p.role" class="input" style="flex:1">
              <option value="vendeur">Vendeur</option>
              <option value="aide">Aide</option>
              <option value="stagiaire">Stagiaire</option>
              <option value="benevole">Bénévole</option>
            </select>
            <button class="btn-remove" @click="form.personnes.splice(i, 1)">✕</button>
          </div>
          <button class="btn-add-personne" @click="form.personnes.push({ nom: '', role: 'vendeur' })">
            + Ajouter une personne
          </button>
        </div>

        <p v-if="erreur" class="error">{{ erreur }}</p>

        <div class="modal-actions">
          <button class="btn btn-secondary" @click="fermerPopup">Annuler</button>
          <button class="btn btn-primary" @click="sauvegarder" :disabled="loading">
            {{ loading ? 'Enregistrement...' : modeEdition ? 'Modifier' : 'Créer' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Popup générateur récurrent -->
    <div class="modal-overlay" v-if="popupGenerateur" @click.self="popupGenerateur = false">
      <div class="modal modal-large">
        <div class="modal-heading">
          <p class="section-kicker">Automatisation</p>
          <h3>Générer le planning récurrent</h3>
        </div>
        <p class="modal-desc">
          Définissez vos règles puis générez tous les marchés automatiquement.
          Les marchés déjà existants ne seront pas écrasés.
        </p>

        <div class="form-row">
          <div class="form-group">
            <label class="label">Date de début</label>
            <input v-model="gen.dateDebut" class="input" type="date" @change="calculerPreview" />
          </div>
          <div class="form-group">
            <label class="label">Date de fin</label>
            <input v-model="gen.dateFin" class="input" type="date" @change="calculerPreview" />
          </div>
        </div>

        <!-- Règles configurables -->
        <div class="regles-config">
          <div class="regles-header">
            <h4>Mes règles récurrentes</h4>
            <button class="btn-add-regle" @click="ajouterRegle">+ Ajouter une règle</button>
          </div>

          <div class="regle-form" v-for="(r, i) in gen.regles" :key="i">
            <select v-model="r.jour" class="input regle-select" @change="calculerPreview">
              <option value="">Jour...</option>
              <option value="1">Lundi</option>
              <option value="2">Mardi</option>
              <option value="3">Mercredi</option>
              <option value="4">Jeudi</option>
              <option value="5">Vendredi</option>
              <option value="6">Samedi</option>
              <option value="0">Dimanche</option>
            </select>
            <div class="commune-autocomplete regle-autocomplete">
              <input
                v-model="r.ville"
                class="input regle-ville"
                placeholder="Tapez une ville ou un code postal"
                autocomplete="off"
                @input="onRegleVilleInput(r, i)"
                @focus="chercherCommunesRegle(r, i)"
              />
              <div class="commune-suggestions regle-suggestions" v-if="regleSuggestionsOuvertes(i)">
                <button
                  v-for="commune in communesRegle(i)"
                  :key="commune.code_insee + '-' + codePostalCommune(commune)"
                  type="button"
                  class="commune-option"
                  @click="selectionnerCommuneRegle(commune, r, i)"
                >
                  <span>{{ commune.nom }}</span>
                  <strong>{{ codesPostauxLabel(commune) }}</strong>
                </button>
                <button
                  v-if="r.ville"
                  type="button"
                  class="commune-option manual"
                  @click="utiliserVilleManuelleRegle(r, i)"
                >
                  <span>Utiliser cette ville manuellement</span>
                  <strong>{{ r.ville }}</strong>
                </button>
              </div>
            </div>
            <input v-model="r.heure_debut" class="input regle-heure" type="time" @change="calculerPreview" />
            <span class="regle-sep">→</span>
            <input v-model="r.heure_fin" class="input regle-heure" type="time" @change="calculerPreview" />
            <button class="btn-remove" @click="gen.regles.splice(i, 1); calculerPreview()">✕</button>
          </div>

          <div class="regles-vide" v-if="gen.regles.length === 0">
            Aucune règle — cliquez sur "+ Ajouter une règle"
          </div>
        </div>

        <div class="gen-preview" v-if="previewGen.length">
          <strong>{{ previewGen.length }} marchés à créer</strong>
        </div>

        <p v-if="erreurGen" class="error">{{ erreurGen }}</p>
        <p v-if="successGen" class="success">{{ successGen }}</p>

        <div class="modal-actions">
          <button class="btn btn-secondary" @click="popupGenerateur = false">Annuler</button>
          <button class="btn btn-primary" @click="genererRecurrents" :disabled="loadingGen || !previewGen.length">
            {{ loadingGen ? 'Génération en cours...' : '⚡ Générer' }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import NavBar from '../components/NavBar.vue'
import api    from '../api'

export default {
  name: 'PlanningView',
  components: { NavBar },
  data() {
    const now = new Date()
    // Date de début = aujourd'hui, date de fin = fin septembre de l'année en cours
    const finSep = now.getFullYear() + '-09-30'
    const debut  = now.toISOString().split('T')[0]
    return {
      annee:       now.getFullYear(),
      mois:        now.getMonth(),
      evenements:  [],
      loading:     false,
      popup:       false,
      modeEdition: false,
      erreur:      '',
      form: {
        id: null, date: '', heure_debut: '07:00', heure_fin: '13:00',
        ville: '', villeAutre: '', notes: '', personnes: [],
        code_postal: '', code_insee: '', latitude: null, longitude: null, marche_lieu_id: null
      },
      communesForm: [],
      communesLoadingForm: false,
      formRechercheTerminee: false,
      formRechercheToken: 0,
      formAutocompleteOpen: false,
      communeSearchTimers: {},
      genCommuneStates: {},
      lieuxCache: null,
      joursLabels: ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'],

      // Popup générateur
      popupGenerateur: false,
      loadingGen:      false,
      erreurGen:       '',
      successGen:      '',
      gen: { dateDebut: debut, dateFin: finSep, regles: [] },
      previewGen: []
    }
  },
  computed: {
    moisStr() {
      return this.annee + '-' + String(this.mois + 1).padStart(2, '0')
    },
    moisAffiche() {
      return new Date(this.annee, this.mois, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
    },
    villesExistantes() {
      return [...new Set(this.evenements.map(e => e.ville).filter(Boolean))].sort()
    },
    formSuggestionsOuvertes() {
      return this.formAutocompleteOpen && (
        this.communesLoadingForm ||
        this.formRechercheTerminee ||
        this.communesForm.length > 0 ||
        Boolean(this.form.ville)
      )
    },

    // Génère les événements récurrents affichés en pointillé
    recurrents() {
      return []
    },

    tousLesEvenements() {
      return [...this.evenements, ...this.recurrents]
        .sort((a, b) => {
          const dateCmp = a.date.localeCompare(b.date)
          if (dateCmp !== 0) return dateCmp
          return a.ville.localeCompare(b.ville)
        })
    },

    grille() {
      const premier = new Date(this.annee, this.mois, 1)
      const dernier = new Date(this.annee, this.mois + 1, 0)
      const today   = new Date().toDateString()

      let debutSemaine = premier.getDay() - 1
      if (debutSemaine < 0) debutSemaine = 6

      const cases = []

      for (let i = debutSemaine - 1; i >= 0; i--) {
        const d = new Date(this.annee, this.mois, -i)
        cases.push({ date: this.dateStr(d), numero: d.getDate(), courant: false, estAujourdhui: false, evenements: [] })
      }

      for (let d = 1; d <= dernier.getDate(); d++) {
        const date  = new Date(this.annee, this.mois, d)
        const dateS = this.dateStr(date)
        cases.push({
          date:          dateS,
          numero:        d,
          courant:       true,
          estAujourdhui: date.toDateString() === today,
          evenements:    this.tousLesEvenements.filter(ev => ev.date.substring(0, 10) === dateS)
        })
      }

      while (cases.length < 42) {
        const d = new Date(this.annee, this.mois + 1, cases.length - debutSemaine - dernier.getDate() + 1)
        cases.push({ date: this.dateStr(d), numero: d.getDate(), courant: false, estAujourdhui: false, evenements: [] })
      }

      return cases
    }
  },
  watch: {
    'form.date'(val) {
      if (!val || this.modeEdition) return
      // Pré-remplir avec la dernière ville utilisée ce jour de la semaine
      const jour = new Date(val).getDay()
      const match = this.evenements.find(e => new Date(e.date).getDay() === jour)
      if (match) {
        this.form.ville = match.ville
        this.resetCommuneFields(this.form)
      }
    },
    'gen.dateDebut'() { this.calculerPreview() },
    'gen.dateFin'()   { this.calculerPreview() }
  },
  methods: {
    dateStr(d) {
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const j = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${j}`
    },
    jourSemaine(d) {
      return new Date(d).toLocaleDateString('fr-FR', { weekday: 'long' })
    },
    formatDateCourt(d) {
      return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })
    },
    couleurVille(ville) {
      const map = {
        'Pézenas':         'bleu',
        'La Grande Motte': 'vert',
        'Sète':            'orange',
        'Mèze':            'violet',
        'Autre':           'gris'
      }
      return map[ville] || 'gris'
    },
    libelleLieu(ev) {
      return ev?.marche_lieu?.nom_affichage || ev?.ville || ''
    },
    estJourFerie(ev) {
      return ev?.contexte_calendrier?.jour_ferie === true
    },
    estVacances(ev) {
      return ev?.contexte_vacances?.en_vacances === true
    },
    zonesVacances(ev) {
      const zones = ev?.contexte_vacances_zones?.zones_en_vacances
      return Array.isArray(zones) ? zones.filter(zone => ['A', 'B', 'C'].includes(zone)) : []
    },
    nomsVacancesZones(ev) {
      const noms = ev?.contexte_vacances_zones?.noms
      return Array.isArray(noms) ? noms.filter(Boolean) : []
    },
    afficherFallbackVacances(ev) {
      return ev?.contexte_vacances_zones?.disponible !== true && this.estVacances(ev)
    },
    libelleFerie(ev, compact = true) {
      const nom = ev?.contexte_calendrier?.nom
      return !compact && nom ? `Férié : ${nom}` : 'Férié'
    },
    libelleVacances(ev, compact = true) {
      const nom = ev?.contexte_vacances?.nom
      return !compact && nom ? `Vacances : ${nom}` : 'Vacances'
    },
    libelleVacancesZones(ev, compact = true) {
      const zones = this.zonesVacances(ev)
      const noms = this.nomsVacancesZones(ev)
      if (compact) return zones.length ? `Vacances ${zones.join(', ')}` : 'Vacances'
      const suffixeNoms = noms.length ? ` : ${noms.join(' / ')}` : ''
      return zones.length ? `Vacances zones ${zones.join(', ')}${suffixeNoms}` : 'Vacances'
    },

    // Retourne les marchés récurrents pour un jour donné (0=dim...6=sam)
    marchesRecurrentsPourJour(jour, ete) {
      const marches = []
      // 3=mer, 5=ven → Sète
      if (jour === 3 || jour === 5) marches.push({ ville: 'Sète' })
      // 4=jeu → Mèze toute l'année + La Grande Motte juin-sept
      if (jour === 4) {
        marches.push({ ville: 'Mèze' })
        if (ete) marches.push({ ville: 'La Grande Motte' })
      }
      // 6=sam → Pézenas
      if (jour === 6) marches.push({ ville: 'Pézenas' })
      // 0=dim → Mèze + La Grande Motte toute l'année
      if (jour === 0) {
        marches.push({ ville: 'Mèze' })
        marches.push({ ville: 'La Grande Motte' })
      }
      return marches
    },

    resetCommuneFields(target) {
      target.code_postal = ''
      target.code_insee = ''
      target.latitude = null
      target.longitude = null
      target.marche_lieu_id = null
      target.nom_affichage = ''
    },
    normalizeCommunes(data) {
      if (Array.isArray(data)) return data
      return data?.communes || data?.data || []
    },
    codePostalCommune(commune) {
      return commune?.code_postal_principal || commune?.code_postal || commune?.codes_postaux?.[0] || ''
    },
    codesPostauxLabel(commune) {
      const codes = commune?.codes_postaux || []
      return codes.length ? codes.join(' / ') : commune?.code_postal_principal || commune?.code_postal || ''
    },
    communePayload(commune) {
      return {
        nom_affichage: commune.nom || '',
        code_postal: this.codePostalCommune(commune),
        code_insee: commune.code_insee || commune.code || '',
        latitude: commune.latitude ?? null,
        longitude: commune.longitude ?? null,
        marche_lieu_id: null
      }
    },
    async rechercherCommunes(q) {
      const recherche = (q || '').trim()
      if (!recherche || (/^\d+$/.test(recherche) ? recherche.length < 5 : recherche.length < 2)) return []
      try {
        const res = await api.get('/marches-lieux/search-communes', { params: { q: recherche } })
        return this.normalizeCommunes(res.data)
      } catch {
        return []
      }
    },
    onFormVilleInput() {
      this.resetCommuneFields(this.form)
      this.formAutocompleteOpen = true
      this.chercherCommunesForm()
    },
    chercherCommunesForm() {
      clearTimeout(this.communeSearchTimers.form)
      const q = (this.form.ville || '').trim()
      if (!q || (/^\d+$/.test(q) ? q.length < 5 : q.length < 2)) {
        this.communesForm = []
        this.communesLoadingForm = false
        this.formRechercheTerminee = false
        return
      }
      const token = ++this.formRechercheToken
      this.communesLoadingForm = true
      this.formRechercheTerminee = false
      this.communeSearchTimers.form = setTimeout(async () => {
        const communes = await this.rechercherCommunes(q)
        if (token !== this.formRechercheToken || q !== (this.form.ville || '').trim()) return
        this.communesForm = communes
        this.communesLoadingForm = false
        this.formRechercheTerminee = true
      }, 300)
    },
    selectionnerCommuneForm(commune) {
      const payload = this.communePayload(commune)
      this.form.ville = commune.nom || ''
      Object.assign(this.form, payload)
      this.communesForm = []
      this.communesLoadingForm = false
      this.formRechercheTerminee = false
      this.formAutocompleteOpen = false
    },
    utiliserVilleManuelleForm() {
      this.resetCommuneFields(this.form)
      this.communesForm = []
      this.communesLoadingForm = false
      this.formRechercheTerminee = false
      this.formAutocompleteOpen = false
    },
    regleState(i) {
      if (!this.genCommuneStates[i]) {
        this.genCommuneStates[i] = { communes: [], loading: false, open: false }
      }
      return this.genCommuneStates[i]
    },
    communesRegle(i) {
      return this.regleState(i).communes
    },
    regleSuggestionsOuvertes(i) {
      const state = this.regleState(i)
      const regle = this.gen.regles[i]
      return state.open && (state.communes.length > 0 || Boolean(regle?.ville))
    },
    onRegleVilleInput(regle, i) {
      this.resetCommuneFields(regle)
      this.regleState(i).open = true
      this.chercherCommunesRegle(regle, i)
      this.calculerPreview()
    },
    chercherCommunesRegle(regle, i) {
      const key = `regle-${i}`
      clearTimeout(this.communeSearchTimers[key])
      const q = (regle.ville || '').trim()
      const state = this.regleState(i)
      if (!q || (/^\d+$/.test(q) ? q.length < 5 : q.length < 2)) {
        state.communes = []
        return
      }
      this.communeSearchTimers[key] = setTimeout(async () => {
        state.loading = true
        state.communes = await this.rechercherCommunes(q)
        state.loading = false
      }, 300)
    },
    selectionnerCommuneRegle(commune, regle, i) {
      Object.assign(regle, {
        ville: commune.nom || '',
        ...this.communePayload(commune)
      })
      const state = this.regleState(i)
      state.communes = []
      state.open = false
      this.calculerPreview()
    },
    utiliserVilleManuelleRegle(regle, i) {
      this.resetCommuneFields(regle)
      const state = this.regleState(i)
      state.communes = []
      state.open = false
      this.calculerPreview()
    },
    async chargerLieux() {
      if (this.lieuxCache) return this.lieuxCache
      try {
        const res = await api.get('/marches-lieux')
        this.lieuxCache = Array.isArray(res.data) ? res.data : []
      } catch {
        this.lieuxCache = []
      }
      return this.lieuxCache
    },
    async assurerMarcheLieu(source) {
      if (!source?.code_insee || !source?.code_postal || !source?.ville) return null
      const lieux = await this.chargerLieux()
      const existant = lieux.find(lieu =>
        lieu.code_insee === source.code_insee && lieu.code_postal === source.code_postal
      )
      if (existant?.id) return existant.id

      try {
        const res = await api.post('/marches-lieux', {
          nom_affichage: source.nom_affichage || source.ville,
          ville: source.ville,
          code_postal: source.code_postal,
          code_insee: source.code_insee,
          latitude: source.latitude,
          longitude: source.longitude,
          zone_jours_feries: 'metropole',
          zone_scolaire: 'C'
        })
        if (res.data?.id) {
          this.lieuxCache = [...lieux, res.data]
          return res.data.id
        }
      } catch (err) {
        if (err.response?.status === 409) {
          this.lieuxCache = null
          const lieuxActualises = await this.chargerLieux()
          return lieuxActualises.find(lieu =>
            lieu.code_insee === source.code_insee && lieu.code_postal === source.code_postal
          )?.id || null
        }
      }
      return null
    },
    villeFinale() {
      return this.form.ville === '__autre__' ? this.form.villeAutre : this.form.ville
    },
    moisPrecedent() {
      if (this.mois === 0) { this.mois = 11; this.annee-- } else { this.mois-- }
      this.charger()
    },
    moisSuivant() {
      if (this.mois === 11) { this.mois = 0; this.annee++ } else { this.mois++ }
      this.charger()
    },
    allerAujourdhui() {
      const now = new Date()
      this.annee = now.getFullYear()
      this.mois  = now.getMonth()
      this.charger()
    },
    clicJour(jour) {
      const rec = this.recurrents.find(r => r.date === jour.date)
      if (rec) {
        this.ouvrirDetail(rec)
      } else {
        this.ouvrirCreer(jour.date)
      }
    },
    ouvrirDetail(ev) {
      this.modeEdition = !ev.recurrent
      this.erreur      = ''
      this.form = {
        id:          ev.recurrent ? null : ev.id,
        date:        ev.date.substring(0, 10),
        heure_debut: ev.heure_debut.substring(0, 5),
        heure_fin:   ev.heure_fin.substring(0, 5),
        ville:       ev.ville,
        villeAutre:   '',
        notes:       ev.notes || '',
        personnes:   ev.personnes ? ev.personnes.map(p => ({ nom: p.nom, role: p.role })) : [],
        code_postal: ev.marche_lieu?.code_postal || ev.code_postal || '',
        code_insee:  ev.marche_lieu?.code_insee || ev.code_insee || '',
        latitude:    ev.marche_lieu?.latitude ?? ev.latitude ?? null,
        longitude:   ev.marche_lieu?.longitude ?? ev.longitude ?? null,
        marche_lieu_id: ev.marche_lieu_id || null
      }
      this.communesForm = []
      this.communesLoadingForm = false
      this.formRechercheTerminee = false
      this.formAutocompleteOpen = false
      this.popup = true
    },
    ouvrirCreer(date) {
      this.modeEdition = false
      this.erreur      = ''
      this.form = {
        id: null, date: date || new Date().toISOString().split('T')[0],
        heure_debut: '07:00', heure_fin: '13:00',
        ville: '', villeAutre: '', notes: '', personnes: [],
        nom_affichage: '', code_postal: '', code_insee: '', latitude: null, longitude: null, marche_lieu_id: null
      }
      this.communesForm = []
      this.communesLoadingForm = false
      this.formRechercheTerminee = false
      this.formAutocompleteOpen = false
      this.popup = true
    },
    fermerPopup() { this.popup = false },
    async charger() {
      this.loading = true
      try {
        const res       = await api.get('/planning?mois=' + this.moisStr)
        this.evenements = res.data
      } catch {} finally { this.loading = false }
    },
    async sauvegarder() {
      this.erreur = ''
      const ville = this.villeFinale()
      if (!this.form.date || !this.form.heure_debut || !this.form.heure_fin || !ville) {
        this.erreur = 'Date, horaires et ville sont obligatoires'
        return
      }
      this.loading = true
      try {
        const marcheLieuId = await this.assurerMarcheLieu({ ...this.form, ville })
        const payload = { ...this.form, ville, marche_lieu_id: marcheLieuId }
        if (this.modeEdition) {
          await api.put('/planning/' + this.form.id, payload)
        } else {
          await api.post('/planning', payload)
        }
        this.popup = false
        await this.charger()
      } catch (err) {
        this.erreur = err.response?.data?.error || 'Erreur serveur'
      } finally { this.loading = false }
    },
    async supprimer(id) {
      if (!confirm('Supprimer ce marche du planning ?')) return
      if (!confirm('Supprimer ce marché ?')) return
      try { await api.delete('/planning/' + id); await this.charger() } catch {}
    },

    // === GÉNÉRATEUR RÉCURRENT ===
    ouvrirGenerateur() {
      this.erreurGen  = ''
      this.successGen = ''
      // Si pas de règles encore, pré-remplir avec les jours déjà utilisés
      if (this.gen.regles.length === 0 && this.evenements.length > 0) {
        const reglesDetectees = {}
        this.evenements.forEach(ev => {
          const jour = new Date(ev.date).getDay()
          const key  = `${jour}-${ev.ville}`
          if (!reglesDetectees[key]) {
            reglesDetectees[key] = { jour: String(jour), ville: ev.ville, heure_debut: ev.heure_debut.substring(0,5), heure_fin: ev.heure_fin.substring(0,5) }
          }
        })
        this.gen.regles = Object.values(reglesDetectees)
      }
      this.calculerPreview()
      this.popupGenerateur = true
    },

    ajouterRegle() {
      this.gen.regles.push({
        jour: '', ville: '', heure_debut: '07:00', heure_fin: '13:00',
        nom_affichage: '', code_postal: '', code_insee: '', latitude: null, longitude: null, marche_lieu_id: null
      })
    },

    calculerPreview() {
      if (!this.gen.dateDebut || !this.gen.dateFin) return
      const debut  = new Date(this.gen.dateDebut)
      const fin    = new Date(this.gen.dateFin)
      const regles = this.gen.regles.filter(r => r.jour !== '' && r.ville.trim() !== '')
      const liste  = []

      for (let d = new Date(debut); d <= fin; d.setDate(d.getDate() + 1)) {
        const jourStr = String(d.getDay())
        const dateStr = this.dateStr(new Date(d))
        for (const r of regles) {
          if (r.jour === jourStr) {
            liste.push({
              date: dateStr,
              ville: r.ville.trim(),
              heure_debut: r.heure_debut,
              heure_fin: r.heure_fin,
              nom_affichage: r.nom_affichage || '',
              code_postal: r.code_postal || '',
              code_insee: r.code_insee || '',
              latitude: r.latitude ?? null,
              longitude: r.longitude ?? null,
              marche_lieu_id: r.marche_lieu_id || null
            })
          }
        }
      }
      this.previewGen = liste
    },

    async genererRecurrents() {
      this.erreurGen  = ''
      this.successGen = ''
      if (!this.gen.dateDebut || !this.gen.dateFin) {
        this.erreurGen = 'Dates requises'
        return
      }
      if (this.previewGen.length === 0) {
        this.erreurGen = 'Aucun marché à générer — vérifiez vos règles et la période'
        return
      }
      if (!confirm(`Créer ${this.previewGen.length} marchés ? Les existants ne seront pas modifiés.`)) return

      this.loadingGen = true
      let crees  = 0
      let skips  = 0
      let errors = 0

      for (const m of this.previewGen) {
        try {
          const marcheLieuId = await this.assurerMarcheLieu(m)
          await api.post('/planning', {
            date:        m.date,
            heure_debut: m.heure_debut || '07:00',
            heure_fin:   m.heure_fin   || '13:00',
            ville:       m.ville,
            marche_lieu_id: marcheLieuId,
            notes:       '',
            personnes:   []
          })
          crees++
        } catch (err) {
          if (err.response?.status === 409 || err.response?.data?.error?.includes('existe')) {
            skips++
          } else {
            errors++
          }
        }
      }

      this.loadingGen = false
      this.successGen = `✅ ${crees} marchés créés${skips ? `, ${skips} déjà existants ignorés` : ''}${errors ? `, ${errors} erreurs` : ''}`
      await this.charger()
    }
  },
  mounted() { this.charger() }
}
</script>

<style scoped>
.planning-page {
  max-width: 1180px;
  margin: 0 auto;
  padding: 28px 20px 44px;
}

.planning-hero {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 20px;
}

.page-kicker,
.section-kicker {
  margin: 0 0 6px;
  color: var(--fm-accent);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

.page-title {
  margin: 0;
  color: var(--fm-navy);
  font-size: 34px;
  font-weight: 800;
  line-height: 1.1;
}

.page-subtitle {
  max-width: 620px;
  margin: 10px 0 0;
  color: var(--fm-muted);
  font-size: 15px;
  line-height: 1.6;
}

.header-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.action-btn {
  width: auto;
  min-height: 48px;
  padding: 12px 18px;
  white-space: nowrap;
}

.planning-shell,
.calendrier,
.liste-card,
.vide-card {
  background: var(--fm-surface);
  border: 1px solid var(--fm-border);
  border-radius: var(--fm-card-radius);
  box-shadow: var(--fm-shadow-subtle);
}

.planning-shell {
  padding: 16px;
  margin-bottom: 16px;
}

.calendar-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
}

.mois-nav {
  display: flex;
  align-items: center;
  gap: 10px;
}

.mois-titre {
  min-width: 220px;
  color: var(--fm-navy);
  font-size: 18px;
  font-weight: 800;
  text-align: center;
  text-transform: capitalize;
}

.nav-btn,
.btn-aujourd-hui,
.btn-action,
.btn-remove,
.btn-add-personne,
.btn-add-regle {
  transition: background var(--fm-transition), border-color var(--fm-transition), color var(--fm-transition), box-shadow var(--fm-transition), transform var(--fm-transition);
}

.nav-btn {
  width: 42px;
  height: 42px;
  border: 1px solid var(--fm-border);
  border-radius: 14px;
  background: var(--fm-surface);
  color: var(--fm-navy);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 700;
}

.nav-btn:hover,
.btn-aujourd-hui:hover {
  border-color: rgba(30, 111, 220, 0.4);
  color: var(--fm-accent);
  background: var(--fm-accent-soft);
}

.btn-aujourd-hui {
  min-height: 42px;
  padding: 0 16px;
  border: 1px solid var(--fm-border);
  border-radius: 14px;
  background: var(--fm-surface);
  color: var(--fm-navy);
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
}

.legende {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  color: var(--fm-muted);
  font-size: 12px;
}

.leg-item {
  display: flex;
  align-items: center;
  gap: 7px;
  min-height: 32px;
  padding: 6px 10px;
  border: 1px solid var(--fm-border);
  border-radius: 999px;
  background: #F8FAFC;
  font-weight: 600;
}

.leg-dot {
  width: 10px;
  height: 10px;
  border-radius: 4px;
  display: inline-block;
}

.leg-dot.ev-orange { background: #FED7AA; border: 1px solid #FB923C; }
.leg-dot.ev-violet { background: #DDD6FE; border: 1px solid #8B5CF6; }
.leg-dot.ev-bleu { background: #DBEAFE; border: 1px solid var(--fm-accent); }
.leg-dot.ev-vert { background: var(--fm-success-soft); border: 1px solid var(--fm-success); }
.leg-dot.ev-gris { background: #F1F5F9; border: 1px solid #94A3B8; }

.loading-card {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  padding: 14px 16px;
  border: 1px solid var(--fm-border);
  border-radius: var(--fm-card-radius);
  background: var(--fm-surface);
  color: var(--fm-muted);
  box-shadow: var(--fm-shadow-subtle);
  font-size: 13px;
  font-weight: 600;
}

.loading-dot {
  width: 9px;
  height: 9px;
  border-radius: 999px;
  background: var(--fm-accent);
  box-shadow: 0 0 0 6px var(--fm-accent-soft);
}

.calendrier {
  overflow: hidden;
  margin-bottom: 20px;
}

.cal-header {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  background: #F8FAFC;
  border-bottom: 1px solid var(--fm-border);
}

.cal-jour-label {
  padding: 14px 10px;
  color: var(--fm-muted);
  text-align: center;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
}

.cal-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
}

.cal-cell {
  min-height: 118px;
  padding: 10px;
  border-right: 1px solid var(--fm-border);
  border-bottom: 1px solid var(--fm-border);
  background: var(--fm-surface);
  cursor: pointer;
  transition: background var(--fm-transition), box-shadow var(--fm-transition);
}

.cal-cell:nth-child(7n) {
  border-right: none;
}

.cal-cell:hover {
  background: #F8FAFC;
  box-shadow: inset 0 0 0 1px rgba(30, 111, 220, 0.16);
}

.cal-cell.autre-mois {
  background: #F8FAFC;
  cursor: default;
}

.cal-cell.autre-mois .cal-num {
  color: #CBD5E1;
}

.cal-cell.aujourd-hui {
  background: #F0FDF4;
}

.cal-cell.aujourd-hui .cal-num {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  background: var(--fm-success);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 18px rgba(34, 197, 94, 0.24);
}

.cal-num {
  margin-bottom: 8px;
  color: var(--fm-text);
  font-size: 13px;
  font-weight: 800;
}

.cal-event {
  display: flex;
  align-items: center;
  gap: 5px;
  min-height: 24px;
  margin-bottom: 5px;
  padding: 4px 7px;
  border: 1px solid transparent;
  border-radius: 9px;
  cursor: pointer;
  overflow: hidden;
  font-size: 10px;
  font-weight: 700;
  transition: transform var(--fm-transition), box-shadow var(--fm-transition);
}

.cal-event:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 16px rgba(15, 23, 42, 0.08);
}

.cal-event.ev-recurrent {
  opacity: 0.72;
  border-style: dashed;
  background: transparent !important;
}

.ev-heure {
  flex-shrink: 0;
  font-weight: 800;
  white-space: nowrap;
}

.ev-ville {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cal-flags {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-left: auto;
  flex-shrink: 0;
}

.cal-flag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 5px;
  border: 1px solid currentColor;
  background: rgba(255, 255, 255, 0.62);
  font-size: 9px;
  font-weight: 900;
  line-height: 1;
}

.flag-ferie {
  color: #B91C1C;
}

.flag-vacances {
  color: #047857;
}

.cal-plus {
  display: inline-flex;
  align-items: center;
  min-height: 22px;
  padding: 2px 7px;
  border-radius: 999px;
  background: #F1F5F9;
  color: var(--fm-muted);
  font-size: 10px;
  font-weight: 800;
}

.ev-bleu { background: #DBEAFE; color: #1E3A8A; border-color: #BFDBFE; }
.ev-vert { background: var(--fm-success-soft); color: #166534; border-color: #BBF7D0; }
.ev-orange { background: #FFEDD5; color: #9A3412; border-color: #FED7AA; }
.ev-violet { background: #EDE9FE; color: #5B21B6; border-color: #DDD6FE; }
.ev-gris { background: #F1F5F9; color: #475569; border-color: var(--fm-border); }

.border-bleu { border-left: 4px solid var(--fm-accent); }
.border-vert { border-left: 4px solid var(--fm-success); }
.border-orange { border-left: 4px solid #F97316; }
.border-violet { border-left: 4px solid #8B5CF6; }
.border-gris { border-left: 4px solid #94A3B8; }

.bg-bleu { background: #DBEAFE; color: #1E3A8A; }
.bg-vert { background: var(--fm-success-soft); color: #166534; }
.bg-orange { background: #FFEDD5; color: #9A3412; }
.bg-violet { background: #EDE9FE; color: #5B21B6; }
.bg-gris { background: #F1F5F9; color: #475569; }

.liste-card {
  padding: 20px;
  margin-bottom: 20px;
}

.section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.liste-card h3,
.vide-card h3,
.modal h3 {
  margin: 0;
  color: var(--fm-navy);
  font-weight: 800;
}

.liste-card h3 {
  font-size: 18px;
  text-transform: capitalize;
}

.count-pill {
  padding: 8px 12px;
  border-radius: 999px;
  background: var(--fm-accent-soft);
  color: var(--fm-accent);
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
}

.ev-liste {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ev-item {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 15px 16px;
  border: 1px solid var(--fm-border);
  border-radius: 16px;
  background: #F8FAFC;
  transition: background var(--fm-transition), border-color var(--fm-transition), transform var(--fm-transition), box-shadow var(--fm-transition);
}

.ev-item:hover {
  background: var(--fm-surface);
  border-color: rgba(30, 111, 220, 0.24);
  box-shadow: var(--fm-shadow-subtle);
  transform: translateY(-1px);
}

.ev-item-recurrent {
  opacity: 0.78;
}

.ev-date-col {
  min-width: 62px;
  padding: 8px 6px;
  border: 1px solid var(--fm-border);
  border-radius: 14px;
  background: var(--fm-surface);
  text-align: center;
  flex-shrink: 0;
}

.ev-jour-sem {
  color: var(--fm-muted);
  font-size: 11px;
  font-weight: 700;
  text-transform: capitalize;
}

.ev-date-num {
  margin-top: 2px;
  color: var(--fm-navy);
  font-size: 18px;
  font-weight: 800;
}

.ev-info {
  flex: 1;
  min-width: 0;
}

.ev-titre {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 5px;
}

.ev-badge {
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
}

.ev-badge em {
  font-style: normal;
  opacity: 0.7;
  font-size: 11px;
}

.ev-horaires,
.ev-notes,
.personne-chip {
  color: var(--fm-muted);
  font-size: 13px;
}

.ev-horaires {
  font-weight: 700;
}

.ev-contextes {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 2px 0 7px;
}

.context-badge {
  max-width: 100%;
  padding: 4px 8px;
  border: 1px solid transparent;
  border-radius: 999px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 11px;
  font-weight: 800;
}

.context-ferie {
  background: #FEE2E2;
  border-color: #FECACA;
  color: #991B1B;
}

.context-vacances {
  background: #D1FAE5;
  border-color: #A7F3D0;
  color: #065F46;
}

.context-vacances-name {
  background: #ECFDF5;
  border-color: #BBF7D0;
  color: #047857;
}

.ev-notes {
  margin-top: 4px;
  line-height: 1.5;
}

.ev-personnes {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 10px;
}

.personne-chip {
  padding: 5px 10px;
  border: 1px solid var(--fm-border);
  border-radius: 999px;
  background: var(--fm-surface);
  font-weight: 600;
}

.personne-role {
  color: #94A3B8;
  font-size: 11px;
}

.ev-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.btn-action {
  min-width: 42px;
  min-height: 42px;
  border: 1px solid var(--fm-border);
  border-radius: 12px;
  background: var(--fm-surface);
  color: var(--fm-navy);
  cursor: pointer;
  font-size: 14px;
}

.btn-action:hover {
  border-color: rgba(30, 111, 220, 0.35);
  background: var(--fm-accent-soft);
}

.btn-action.delete:hover {
  border-color: #FCA5A5;
  background: var(--fm-danger-soft);
}

.vide-card {
  padding: 42px 24px;
  color: var(--fm-muted);
  text-align: center;
}

.empty-icon {
  width: 48px;
  height: 48px;
  margin: 0 auto 14px;
  border-radius: 16px;
  background: var(--fm-accent-soft);
  color: var(--fm-accent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  font-weight: 700;
}

.vide-card p {
  max-width: 420px;
  margin: 8px auto 18px;
  font-size: 14px;
  line-height: 1.6;
}

.empty-action {
  width: auto;
  min-height: 48px;
  margin: 0 auto;
  padding: 12px 18px;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  overflow-y: auto;
  background: rgba(13, 27, 61, 0.48);
  backdrop-filter: blur(6px);
}

.modal {
  width: 100%;
  max-width: 560px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 26px;
  border: 1px solid rgba(226, 232, 240, 0.9);
  border-radius: 22px;
  background: var(--fm-surface);
  box-shadow: 0 24px 70px rgba(13, 27, 61, 0.24);
}

.modal-large {
  max-width: 740px;
}

.modal-heading {
  margin-bottom: 16px;
}

.modal h3 {
  font-size: 20px;
}

.modal-desc {
  margin: -4px 0 18px;
  color: var(--fm-muted);
  font-size: 13px;
  line-height: 1.6;
}

.form-row {
  display: flex;
  gap: 12px;
}

.form-group {
  flex: 1;
  margin-bottom: 14px;
}

.label {
  display: block;
  margin-bottom: 7px;
  color: var(--fm-text);
  font-size: 13px;
  font-weight: 700;
}

.input {
  width: 100%;
  min-height: 46px;
  padding: 11px 13px;
  border: 1px solid var(--fm-border);
  border-radius: 14px;
  outline: none;
  box-sizing: border-box;
  background: var(--fm-surface);
  color: var(--fm-text);
  font: inherit;
  font-size: 14px;
  transition: border-color var(--fm-transition), box-shadow var(--fm-transition), background var(--fm-transition);
}

.input:focus {
  border-color: var(--fm-accent);
  box-shadow: 0 0 0 4px rgba(30, 111, 220, 0.12);
}

.commune-autocomplete {
  position: relative;
}

.commune-suggestions {
  position: absolute;
  z-index: 20;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  max-height: 260px;
  overflow-y: auto;
  padding: 6px;
  border: 1px solid var(--fm-border);
  border-radius: 14px;
  background: var(--fm-surface);
  box-shadow: 0 18px 42px rgba(13, 27, 61, 0.18);
}

.commune-option {
  width: 100%;
  min-height: 48px;
  padding: 10px 12px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: var(--fm-text);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  text-align: left;
  font: inherit;
  font-size: 13px;
}

.commune-option:hover {
  background: #F8FAFC;
}

.commune-option-state {
  cursor: default;
  color: var(--fm-muted);
  font-weight: 800;
}

.commune-option-state:hover {
  background: transparent;
}

.commune-option span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 800;
}

.commune-option strong {
  color: var(--fm-muted);
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.commune-option.manual {
  border-top: 1px solid var(--fm-border);
  border-radius: 0 0 10px 10px;
  color: var(--fm-accent);
}

.commune-state {
  margin: 7px 0 0;
  color: var(--fm-muted);
  font-size: 12px;
  font-weight: 700;
}

.commune-state.selected {
  color: #166534;
}

.regles-config {
  padding: 16px;
  margin-bottom: 16px;
  border: 1px solid var(--fm-border);
  border-radius: var(--fm-card-radius);
  background: #F8FAFC;
}

.regles-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.regles-header h4 {
  margin: 0;
  color: var(--fm-navy);
  font-size: 14px;
  font-weight: 800;
}

.btn-add-regle,
.btn-add-personne {
  border: 1px dashed rgba(30, 111, 220, 0.45);
  background: var(--fm-surface);
  color: var(--fm-accent);
  cursor: pointer;
  font-weight: 800;
}

.btn-add-regle {
  min-height: 40px;
  padding: 8px 14px;
  border-radius: 12px;
  font-size: 13px;
}

.btn-add-regle:hover,
.btn-add-personne:hover {
  background: var(--fm-accent-soft);
  border-color: var(--fm-accent);
}

.regle-form {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.regle-select { flex: 0 0 128px; min-width: 0; }
.regle-autocomplete { flex: 1; min-width: 190px; }
.regle-ville { min-width: 0; }
.regle-heure { flex: 0 0 106px; min-width: 0; }

.regle-sep {
  color: var(--fm-muted);
  font-weight: 800;
  flex-shrink: 0;
}

.regles-vide {
  padding: 18px 0;
  color: var(--fm-muted);
  text-align: center;
  font-size: 13px;
  font-weight: 600;
}

.gen-preview {
  margin-bottom: 12px;
  padding: 12px 14px;
  border: 1px solid #BBF7D0;
  border-radius: 14px;
  background: var(--fm-success-soft);
  color: #166534;
  font-size: 14px;
}

.personnes-section {
  margin-bottom: 16px;
}

.personne-ligne {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}

.btn-remove {
  min-width: 42px;
  min-height: 42px;
  border: 1px solid #FECACA;
  border-radius: 12px;
  background: var(--fm-danger-soft);
  color: var(--fm-danger);
  cursor: pointer;
  font-size: 16px;
  font-weight: 800;
  flex-shrink: 0;
}

.btn-remove:hover {
  background: #FECACA;
}

.btn-add-personne {
  width: 100%;
  min-height: 46px;
  margin-top: 10px;
  padding: 10px 14px;
  border-radius: 14px;
  font-size: 13px;
}

.modal-actions {
  display: flex;
  gap: 10px;
  margin-top: 18px;
}

.btn {
  min-height: 46px;
  padding: 11px 18px;
  border: none;
  border-radius: 14px;
  cursor: pointer;
  flex: 1;
  font: inherit;
  font-size: 14px;
  font-weight: 800;
  transition: background var(--fm-transition), border-color var(--fm-transition), color var(--fm-transition), box-shadow var(--fm-transition), transform var(--fm-transition);
}

.btn-primary {
  background: linear-gradient(180deg, #247BEA 0%, var(--fm-accent) 100%);
  color: white;
  box-shadow: 0 10px 22px rgba(30, 111, 220, 0.22);
}

.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 14px 28px rgba(30, 111, 220, 0.26);
}

.btn-primary:disabled {
  background: #CBD5E1;
  box-shadow: none;
  cursor: not-allowed;
  transform: none;
}

.btn-secondary {
  border: 1px solid var(--fm-border);
  background: var(--fm-surface);
  color: var(--fm-navy);
}

.btn-secondary:hover {
  background: #F8FAFC;
  border-color: rgba(30, 111, 220, 0.28);
}

.error,
.success {
  margin: 0 0 10px;
  padding: 11px 13px;
  border-radius: 14px;
  font-size: 13px;
  font-weight: 700;
}

.error {
  border: 1px solid #FECACA;
  background: var(--fm-danger-soft);
  color: #B91C1C;
}

.success {
  border: 1px solid #BBF7D0;
  background: var(--fm-success-soft);
  color: #166534;
}

@media (max-width: 900px) {
  .planning-hero {
    align-items: flex-start;
    flex-direction: column;
  }

  .header-actions {
    width: 100%;
    justify-content: flex-start;
  }

  .header-actions .btn {
    flex: 1;
  }

  .cal-cell {
    min-height: 100px;
    padding: 8px;
  }

  .cal-event {
    min-height: 22px;
    padding: 3px 6px;
  }
}

@media (max-width: 700px) {
  .planning-page {
    padding: 18px 12px 96px;
  }

  .page-title {
    font-size: 28px;
  }

  .page-subtitle {
    font-size: 14px;
  }

  .header-actions,
  .calendar-toolbar,
  .section-heading {
    flex-direction: column;
    align-items: stretch;
  }

  .action-btn,
  .btn-aujourd-hui,
  .empty-action {
    width: 100%;
  }

  .mois-nav {
    justify-content: space-between;
  }

  .mois-titre {
    min-width: 0;
    flex: 1;
    font-size: 16px;
  }

  .planning-shell,
  .liste-card {
    padding: 14px;
  }

  .legende {
    gap: 8px;
  }

  .leg-item {
    flex: 1 1 calc(50% - 8px);
    justify-content: center;
  }

  .calendrier {
    border-radius: 16px;
  }

  .cal-jour-label {
    padding: 10px 2px;
    font-size: 10px;
  }

  .cal-cell {
    min-height: 78px;
    padding: 5px;
  }

  .cal-num {
    margin-bottom: 5px;
    font-size: 12px;
  }

  .cal-cell.aujourd-hui .cal-num {
    width: 24px;
    height: 24px;
    font-size: 11px;
  }

  .cal-event {
    min-height: 18px;
    margin-bottom: 3px;
    padding: 2px 4px;
    border-radius: 7px;
    font-size: 9px;
    gap: 3px;
  }

  .cal-flag {
    width: 13px;
    height: 13px;
    border-radius: 4px;
    font-size: 8px;
  }

  .cal-flags {
    gap: 2px;
  }

  .cal-event .ev-heure {
    display: none;
  }

  .cal-plus {
    min-height: 18px;
    padding: 1px 5px;
    font-size: 9px;
  }

  .ev-item {
    flex-wrap: wrap;
    gap: 12px;
    padding: 14px;
  }

  .ev-date-col {
    min-width: 58px;
  }

  .ev-info {
    flex: 1 1 calc(100% - 76px);
  }

  .context-badge {
    max-width: 100%;
    padding: 4px 7px;
    font-size: 10px;
  }

  .ev-actions {
    width: 100%;
  }

  .btn-action {
    flex: 1;
    min-height: 48px;
  }

  .form-row,
  .personne-ligne {
    flex-direction: column;
    align-items: stretch;
  }

  .modal-overlay {
    align-items: flex-start;
    padding: 12px;
  }

  .modal {
    padding: 20px;
    border-radius: 18px;
  }

  .modal-actions {
    flex-direction: column-reverse;
  }

  .regles-header {
    align-items: stretch;
    flex-direction: column;
  }

  .regle-form {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .regle-select,
  .regle-autocomplete,
  .regle-heure {
    flex: none;
    min-width: 0;
  }

  .regle-autocomplete {
    grid-column: 1 / -1;
  }

  .regle-sep {
    display: none;
  }

  .regle-form .btn-remove {
    grid-column: 1 / -1;
    min-height: 48px;
  }
}
</style>
